<template>
  <OurPage data-cy="categories-page" :title="$t('title.categories._')" gray class="users-page">
    <CategoriesList
      ref="categoriesList"
      :items="categoriesList"
      :headers="categoriesListHeaders"
      :pagination-data="paginationData"
      :tree-items="treeCategoriesList"
      :tree-filters="treeCategoriesFilters"
      :is-loading="isLoading"
      @pageChange="onPageChange"
      @onClickRow="onClickRow"
      @onDeleteCategory="onClickDelete"
    />

    <CategoriesDeleteModal
      :category-id="categoryId"
      :show.sync="isShownDeleteModal"
      @deleteCategory="getData"
    />

    <FilterModal :show.sync="isShownFilterModal" />

    <template #header-right>
      <div class="flex items-center justify-between gap-4 sm:justify-start">
        <OurButton
          variant="thirdary"
          filled
          :text="$t('button.filters')"
          @click="onClickFilterButton"
        >
          <OurSvgIcon v-if="isMobileDevice" :src="icons.FilterAltFill" />
        </OurButton>

        <TableColumnsSettings
          v-if="!isMobileDevice && isTableColumnsReady"
          :headers="tableHeaders"
          :table-key="tablePreferenceKey"
          data-cy="categories-list-columns"
        />

        <OurLink v-if="getPermission['catalog_category.create']" :route="{ name: 'CategoriesAdd' }">
          <OurButton :text="$t('button.add')" />
        </OurLink>
      </div>
    </template>
  </OurPage>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import apolloMixin from "@/mixins/apollo.mixin";
import CategoriesList from "@/views/settings/categories/index/_components/CategoriesList.vue";
import FilterModal from "@/views/settings/categories/filter/Modal.vue";
import tableColumnsMixin from "@/mixins/tableColumns.mixin";
import TableColumnsSettings from "@/components/TableColumnsSettings.vue";
import { buildCategoriesListTableHeaders } from "@/views/settings/categories/index/config/categoriesListTableHeaders";
import { catalogCategoriesQuery } from "@/views/settings/categories/index/gql/queries";
import DateService from "@/services/date.service";
import CategoriesDeleteModal from "../delete/Modal.vue";

export default {
  name: "Categories",

  components: { CategoriesList, FilterModal, CategoriesDeleteModal, TableColumnsSettings },

  mixins: [apolloMixin, tableColumnsMixin],

  data() {
    return {
      categoriesList: [],
      paginationData: {},
      isShownFilterModal: false,
      isShownDeleteModal: false,
      categoryId: "",
      isLoading: false,
      tableColumnsId: "categories-list",
    };
  },

  computed: {
    ...mapState("categories", ["filtersForm"]),
    ...mapGetters("breakpoint", ["isMobileDevice"]),
    ...mapGetters("categories", ["getStatus", "getFiltersOptions"]),
    ...mapGetters("catalogCategories", ["getCatalogCategoryRecords"]),
    ...mapGetters("user", ["getPermission"]),

    icons: () => ({
      FilterAltFill: require("@material-symbols/svg-400/outlined/filter_alt-fill.svg"),
    }),

    tableHeaders() {
      return buildCategoriesListTableHeaders({
        t: (key) => this.$t(key),
      });
    },

    categoriesListHeaders() {
      if (!this.isTableColumnsReady) {
        return null;
      }

      return this.visibleTableHeaders;
    },

    treeCategoriesFilters() {
      return {
        isActive: null,
        forSidebar: true,
        ...(this.getFiltersOptions || {}),
      };
    },

    treeCategoriesList() {
      return this.getCatalogCategoryRecords.map((category) => this.prepareCategory(category));
    },
  },

  watch: {
    filtersForm: {
      handler: "onChangeFiltersForm",
      deep: true,
    },
  },

  methods: {
    async onPageChange(page) {
      try {
        await this.getData(page);
      } finally {
        this.$refs.categoriesList?.completePageChange?.();
      }
    },

    async getData(page = parseInt(this.$route.query.page) ?? 1) {
      this.isLoading = true;

      try {
        const filtersOptions = this.getFiltersOptions;

        const { catalogCategories } = await this.$get(catalogCategoriesQuery, {
          filters: filtersOptions,
          page,
        });

        if (catalogCategories) {
          this.categoriesList = catalogCategories.data.map((category) =>
            this.prepareCategory(category)
          );

          this.paginationData = catalogCategories.paginatorInfo;
        }
      } finally {
        this.isLoading = false;
      }
    },

    prepareCategory(category) {
      const { id, name, sort, isActive, createdAt, parent, parentId } = category;

      return {
        id,
        name,
        sort: `${sort}`,
        parent: {
          id: parentId,
          name: parent?.name || "-",
        },
        status: this.getStatus(isActive),
        createdAt: createdAt ? DateService.dateConverter(createdAt, "datetime") : "–",
        page: "CategoriesEdit",
      };
    },

    onClickFilterButton() {
      this.isShownFilterModal = true;
    },

    onChangeFiltersForm() {
      this.getData();
    },

    onClickRow(id) {
      this.$router.push({ name: "CategoriesEdit", params: { id } });
    },

    onClickDelete(id) {
      this.categoryId = id;
      this.isShownDeleteModal = true;
    },
  },
};
</script>
